import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Shield, Zap, Globe, ArrowLeft, Users, Target, Heart } from 'lucide-react';

const About: React.FC = () => {
    return (
        <div className="min-h-screen bg-white dark:bg-[#0a0a0b]">
            {/* Header */}
            <header className="bg-white dark:bg-[#0a0a0b] border-b border-gray-100 dark:border-white/5">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <Link to="/" className="flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-white transition-colors">
                            <ArrowLeft size={20} className="mr-2" />
                            Back to Home
                        </Link>
                        <Link to="/" className="flex items-center space-x-2">
                            <div className="p-2 bg-gradient-to-tr from-blue-600 to-indigo-600 rounded-xl shadow-lg">
                                <Mail className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-bold text-xl text-gray-900 dark:text-white">Lucky<span className="text-blue-600">Send</span></span>
                        </Link>
                        <div className="w-28" />
                    </div>
                </div>
            </header>

            <main>
                {/* Hero */}
                <section className="py-20 bg-gradient-to-b from-blue-50 dark:from-[#0f0f18] to-white dark:to-[#0a0a0b]">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 mb-6">
                            <Heart className="w-4 h-4 text-blue-600" />
                            <span className="text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-widest">Our Story</span>
                        </div>
                        <h1 className="text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-6 tracking-tight">
                            About LuckySend
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
                            We built LuckySend because sending a beautifully formatted HTML email with attachments should be
                            simple — not a 45-minute ordeal involving SMTP configuration and app passwords.
                        </p>
                    </div>
                </section>

                {/* Who We Are */}
                <section className="py-16 bg-white dark:bg-[#0a0a0b]">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                            <div>
                                <div className="flex items-center space-x-3 mb-6">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                                        <Users className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <h2 className="text-2xl font-black text-gray-900 dark:text-white">Who We Are</h2>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                                    LuckySend is built and maintained by <strong className="text-gray-800 dark:text-white">Forouverse Solutions</strong>,
                                    a small software team passionate about developer productivity and modern web tools.
                                </p>
                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                                    We noticed that marketers, support teams, and developers often need to send one-off HTML emails quickly —
                                    whether it's a customized invoice, an HTML newsletter draft, or a file-heavy business communication.
                                    Existing tools were either too complex, too expensive, or required storing your credentials on
                                    someone else's servers.
                                </p>
                                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                                    So we built LuckySend: a free, open-standards tool that uses Google's own OAuth 2.0 API to send
                                    email securely from your own Gmail account, directly. No middleman. No stored passwords.
                                </p>
                            </div>
                            <div className="space-y-4">
                                {[
                                    { icon: Shield, title: 'Privacy First', desc: 'We never store your password or long-lived credentials. OAuth access tokens live only in your browser.' },
                                    { icon: Zap, title: 'Built for Speed', desc: 'Compose, attach, and send in under 60 seconds. No lengthy onboarding or complicated setup.' },
                                    { icon: Globe, title: 'Globally Accessible', desc: 'LuckySend works from any device, any browser, anywhere in the world with no software to install.' },
                                ].map((item) => (
                                    <div key={item.title} className="flex space-x-4 p-4 bg-gray-50 dark:bg-[#121214] rounded-2xl border border-gray-100 dark:border-white/5">
                                        <div className="w-10 h-10 bg-white dark:bg-white/10 rounded-xl flex items-center justify-center shrink-0 shadow-sm">
                                            <item.icon className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 dark:text-white mb-1">{item.title}</h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Our Mission */}
                <section className="py-16 bg-gray-50 dark:bg-[#0f0f11] border-y border-gray-100 dark:border-white/5">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <div className="flex items-center justify-center space-x-3 mb-6">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                                <Target className="w-5 h-5 text-blue-600" />
                            </div>
                            <h2 className="text-2xl font-black text-gray-900 dark:text-white">Our Mission</h2>
                        </div>
                        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-6">
                            To make professional email communication accessible to everyone — from solo freelancers to growing teams —
                            without sacrificing security, privacy, or ease of use.
                        </p>
                        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
                            LuckySend is 100% free. We keep the lights on through Google AdSense advertising displayed on public
                            pages of the site. We are committed to keeping the core email-sending features free forever.
                        </p>
                    </div>
                </section>

                {/* How It Works */}
                <section className="py-16 bg-white dark:bg-[#0a0a0b]">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-10 text-center">What LuckySend Does</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                {
                                    step: '01',
                                    title: 'Connect Your Gmail',
                                    desc: 'Sign in with your Google account using the official OAuth 2.0 flow. LuckySend never sees your password — Google handles authentication entirely.'
                                },
                                {
                                    step: '02',
                                    title: 'Compose Freely',
                                    desc: 'Write rich HTML emails using our built-in composer or paste your own HTML template. Attach PDFs, images, or any file type up to Gmail\'s limits.'
                                },
                                {
                                    step: '03',
                                    title: 'Send & Verify',
                                    desc: 'Hit send and watch real-time delivery confirmation. Emails are sent directly from your Gmail account — recipients see your address, not a third party.'
                                }
                            ].map((item) => (
                                <div key={item.step} className="relative">
                                    <div className="text-6xl font-black text-gray-100 dark:text-white/5 absolute -top-6 -left-2">{item.step}</div>
                                    <div className="relative pt-4">
                                        <h3 className="text-lg font-black text-gray-900 dark:text-white mb-3">{item.title}</h3>
                                        <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-sm">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Contact */}
                <section className="py-16 bg-blue-600 relative overflow-hidden">
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff10_1px,transparent_1px),linear-gradient(to_bottom,#ffffff10_1px,transparent_1px)] bg-[size:40px_40px]" />
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                        <h2 className="text-3xl font-black text-white mb-4">Get in Touch</h2>
                        <p className="text-blue-100 mb-8 max-w-xl mx-auto leading-relaxed">
                            Have a question, found a bug, or want to share feedback? We'd love to hear from you.
                            LuckySend is an actively maintained project and user feedback shapes our roadmap.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <a
                                href="mailto:support@luckysend.app"
                                className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-black shadow-2xl hover:scale-105 active:scale-95 transition-all inline-flex items-center justify-center gap-2"
                            >
                                <Mail className="w-5 h-5" />
                                support@luckysend.app
                            </a>
                            <a
                                href="https://forouverse.solutions"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-blue-700 text-white px-8 py-4 rounded-2xl font-black border border-blue-500 hover:scale-105 active:scale-95 transition-all inline-flex items-center justify-center gap-2"
                            >
                                <Globe className="w-5 h-5" />
                                forouverse.solutions
                            </a>
                        </div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="bg-white dark:bg-[#0a0a0b] border-t border-gray-100 dark:border-white/5 py-10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
                    <div className="flex flex-wrap justify-center gap-6 mb-6">
                        <Link to="/" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">Home</Link>
                        <Link to="/terms" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">Terms</Link>
                        <Link to="/privacy" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">Privacy</Link>
                        <a href="mailto:support@luckysend.app" className="text-sm font-bold text-gray-400 hover:text-gray-900 dark:hover:text-white">Contact</a>
                    </div>
                    <p className="text-xs font-medium text-gray-400">© 2026 LuckySend. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default About;
